import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Container, Chip,
  Avatar, Button, useTheme, alpha, Paper, IconButton,
  Divider, List, ListItem, ListItemIcon, ListItemText, Breadcrumbs, CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  MenuOpen as MenuOpenIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Public as PublicIcon,
  Timeline as TimelineIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  Group as TeamIcon,
  Lightbulb as LightbulbIcon,
  EmojiObjects as InnovationIcon,
  Launch as LaunchIcon,
  Visibility
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import * as marketplaceApi from '../services/marketplace';
import Sidebar from '../components/common/Sidebar';
import ContactModal from '../components/marketplace/ContactModal';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 240;

const MainContent = styled(Box)<{ isSidebarOpen: boolean }>(({ theme, isSidebarOpen }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isSidebarOpen ? drawerWidth : 0,
  width: '100%'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  position: 'relative',
  paddingBottom: theme.spacing(1.5),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);
const MotionCard = motion(Card);

const OpportunityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();

  const [opportunity, setOpportunity] = useState<marketplaceApi.MarketplaceProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [similarProjects, setSimilarProjects] = useState<marketplaceApi.MarketplaceProject[]>([]);

  useEffect(() => {
    const loadOpportunity = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await marketplaceApi.getProjectById(id);
        setOpportunity(data);

        // Load similar projects
        const allProjects = await marketplaceApi.getAllProjects({ sector: data.sector });
        setSimilarProjects(allProjects.filter(p => p.id !== id).slice(0, 3));
      } catch (error) {
        console.error('Error loading opportunity:', error);
        setOpportunity(null);
      } finally {
        setLoading(false);
      }
    };

    loadOpportunity();
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!opportunity) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Opportunity Not Found</Typography>
        <Typography variant="body1" paragraph>
          The investment opportunity you're looking for could not be found.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/marketplace')}
          sx={{ mt: 2 }}
        >
          Back to Marketplace
        </Button>
      </Container>
    );
  }

  const ownerName = opportunity.user
    ? `${opportunity.user.firstName} ${opportunity.user.lastName}`
    : opportunity.owner;

  return (
    <Box sx={{ display: 'flex' }}>
      
      <MainContent isSidebarOpen={false}>
        
        <Container maxWidth="lg" sx={{ py: 6 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 3 }}>
            <Typography 
              color="inherit" 
              sx={{ 
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' } 
              }}
              onClick={() => navigate('/')}
            >
              Home
            </Typography>
            <Typography 
              color="inherit" 
              sx={{ 
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' } 
              }}
              onClick={() => navigate('/marketplace')}
            >
              Marketplace
            </Typography>
            <Typography color="text.primary">
              {opportunity.projectName}
            </Typography>
          </Breadcrumbs>

          {/* Back button */}
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/marketplace')}
            sx={{ mb: 3 }}
          >
            Back to Marketplace
          </Button>

          {/* Hero section with project name and key info */}
          <MotionPaper
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            elevation={2}
            sx={{
              mb: 4,
              p: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)}, ${alpha(theme.palette.primary.dark, 0.9)})`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '30%',
                height: '100%',
                opacity: 0.1,
                background: `radial-gradient(circle, ${theme.palette.common.white} 0%, transparent 70%)`,
                transform: 'translateX(30%)'
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="overline" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                  Investment Opportunity
                </Typography>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
                  {opportunity.projectName}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip 
                    icon={<BusinessIcon />} 
                    label={opportunity.sector} 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                      color: 'white',
                      fontWeight: 500
                    }} 
                  />
                  <Chip 
                    icon={<LocationIcon />} 
                    label={opportunity.country} 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                      color: 'white',
                      fontWeight: 500
                    }} 
                  />
                  <Chip 
                    label={opportunity.stage} 
                    sx={{ 
                      bgcolor: 
                        opportunity.stage === "Ready-to-Build" 
                          ? alpha(theme.palette.success.main, 0.7) 
                          : opportunity.stage === "Feasibility" 
                            ? alpha(theme.palette.info.main, 0.7) 
                            : alpha(theme.palette.warning.main, 0.7),
                      color: 'white',
                      fontWeight: 500
                    }} 
                  />
                </Box>

                <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                  {opportunity.summary}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <MotionBox
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      position: 'relative',
                      background: theme.palette.mode === 'dark'
                        ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.primary.main, 0.03)})`
                        : 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: theme.shadows[8],
                        transform: 'translateY(-4px)',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    {/* Decorative gradient header */}
                    <Box
                      sx={{
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.light})`
                      }}
                    />

                    {/* Company Header Section */}
                    <Box
                      sx={{
                        position: 'relative',
                        background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.03)})`,
                        p: 3,
                        pb: 2
                      }}
                    >
                      {/* Decorative element */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: 100,
                          height: 100,
                          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
                          borderRadius: '50%',
                          transform: 'translate(30%, -30%)'
                        }}
                      />

                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                        <Avatar
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(opportunity.organization)}&background=random&size=100&bold=true`}
                          alt={opportunity.organization}
                          sx={{
                            width: 90,
                            height: 90,
                            border: `4px solid ${theme.palette.background.paper}`,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                            mb: 1.5,
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            }
                          }}
                        />

                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center', mb: 0.3, fontSize: '1.05rem', lineHeight: 1.3 }}>
                          {opportunity.owner}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.85rem', mb: 1.5 }}>
                          {opportunity.organization}
                        </Typography>

                        {/* Inline Stats Chips */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                          <Chip
                            icon={<Visibility sx={{ fontSize: '1rem' }} />}
                            label={`${opportunity.viewCount || 0} views`}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                            }}
                          />
                          <Chip
                            icon={<TimelineIcon sx={{ fontSize: '1rem' }} />}
                            label={opportunity.stage}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              color: 'success.main',
                              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ p: 2.5 }}>
                      {/* Quick Info Cards */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            borderColor: alpha(theme.palette.primary.main, 0.15)
                          }
                        }}>
                          <LocationIcon sx={{ color: 'primary.main', mr: 1.5, fontSize: '1.2rem' }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Location
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                              {opportunity.country}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.secondary.main, 0.03),
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.secondary.main, 0.06),
                            borderColor: alpha(theme.palette.secondary.main, 0.15)
                          }
                        }}>
                          <BusinessIcon sx={{ color: 'secondary.main', mr: 1.5, fontSize: '1.2rem' }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Industry
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                              {opportunity.sector}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* CTA Buttons */}
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={() => setContactModalOpen(true)}
                        disabled={!currentUser || currentUser.id === opportunity.userId}
                        sx={{
                          mb: 1.5,
                          borderRadius: 2.5,
                          py: 1.5,
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          textTransform: 'none',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                          '&:hover': {
                            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease'
                          },
                          '&:active': {
                            transform: 'translateY(0)'
                          }
                        }}
                      >
                        💼 Express Interest
                      </Button>

                      <Button
                        variant="outlined"
                        fullWidth
                        size="medium"
                        onClick={() => setContactModalOpen(true)}
                        disabled={!currentUser || currentUser.id === opportunity.userId}
                        sx={{
                          borderRadius: 2.5,
                          py: 1.25,
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textTransform: 'none',
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            transform: 'translateY(-1px)',
                            transition: 'all 0.2s'
                          }
                        }}
                      >
                        📄 Request Information
                      </Button>
                    </Box>
                  </Paper>
                </MotionBox>
              </Grid>
            </Grid>
          </MotionPaper>

          <Grid container spacing={4}>
            {/* Project Details Section */}
            <Grid item xs={12} md={8}>
              <MotionCard
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -4, transition: { duration: 0.3 } }}
                elevation={2}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderColor: alpha(theme.palette.primary.main, 0.3)
                  }
                }}
              >
                <Box
                  sx={{
                    height: 6,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                  }}
                />
                <CardContent sx={{ p: 4 }}>
                  <SectionTitle variant="h5">
                    Project Overview
                  </SectionTitle>

                  <Typography variant="body1" paragraph>
                    {opportunity.summary}
                  </Typography>

                  {opportunity.keyActivities && opportunity.keyActivities.length > 0 && (
                    <>
                      <SectionTitle variant="h6" sx={{ mt: 4 }}>
                        Key Activities
                      </SectionTitle>
                      <List>
                        {opportunity.keyActivities.map((item, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemIcon>
                              <CheckCircleIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={item.activity} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}

                  {opportunity.productDescription && (
                    <>
                      <SectionTitle variant="h6" sx={{ mt: 4 }}>
                        Product Description
                      </SectionTitle>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {opportunity.productDescription}
                      </Typography>
                    </>
                  )}

                  {opportunity.advantages && (
                    <>
                      <SectionTitle variant="h6" sx={{ mt: 4 }}>
                        Competitive Advantages
                      </SectionTitle>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {opportunity.advantages}
                      </Typography>
                    </>
                  )}

                  {opportunity.nextSteps && (
                    <>
                      <SectionTitle variant="h6" sx={{ mt: 4 }}>
                        Next Steps
                      </SectionTitle>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {opportunity.nextSteps}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </MotionCard>

              {opportunity.documents && opportunity.documents.length > 0 && (
                <MotionCard
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  whileHover={{ y: -4, transition: { duration: 0.3 } }}
                  elevation={2}
                  sx={{
                    mb: 4,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.1)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      boxShadow: `0 8px 32px ${alpha(theme.palette.info.main, 0.2)}`,
                      borderColor: alpha(theme.palette.info.main, 0.3)
                    }
                  }}
                >
                  <Box
                    sx={{
                      height: 6,
                      background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.info.light})`
                    }}
                  />
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle variant="h5">
                      Supporting Documents
                    </SectionTitle>
                    <List>
                      {opportunity.documents.map((doc, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <DescriptionIcon color="info" />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.name}
                            secondary="Click to download"
                          />
                          <Button
                            variant="text"
                            color="primary"
                            endIcon={<LaunchIcon />}
                            onClick={() => window.open(doc.url, '_blank')}
                          >
                            View
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </MotionCard>
              )}

              {opportunity.links && opportunity.links.length > 0 && (
                <MotionCard
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  whileHover={{ y: -4, transition: { duration: 0.3 } }}
                  elevation={2}
                  sx={{
                    mb: 4,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.1)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      boxShadow: `0 8px 32px ${alpha(theme.palette.secondary.main, 0.2)}`,
                      borderColor: alpha(theme.palette.secondary.main, 0.3)
                    }
                  }}
                >
                  <Box
                    sx={{
                      height: 6,
                      background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`
                    }}
                  />
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle variant="h5">
                      External Links
                    </SectionTitle>
                    <List>
                      {opportunity.links.map((link, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <PublicIcon color="secondary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={link.title || link.url}
                          />
                          <Button
                            variant="text"
                            color="secondary"
                            endIcon={<LaunchIcon />}
                            onClick={() => window.open(link.url, '_blank')}
                          >
                            Visit
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </MotionCard>
              )}
            </Grid>

            {/* Sidebar Section */}
            <Grid item xs={12} md={4}>
              {/* Project Status Card */}
              <MotionCard
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -4, transition: { duration: 0.3 } }}
                elevation={2}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.1)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.2)}`,
                    borderColor: alpha(theme.palette.success.main, 0.3)
                  }
                }}
              >
                <Box
                  sx={{
                    height: 6,
                    background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`
                  }}
                />
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle variant="h5">
                    Project Status
                  </SectionTitle>
                  
                  <List disablePadding>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <TimelineIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Current Stage" 
                        secondary={opportunity.stage}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 600 }}
                      />
                    </ListItem>
                    
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <LocationIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Location" 
                        secondary={opportunity.country}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 600 }}
                      />
                    </ListItem>
                    
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <BusinessIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Industry" 
                        secondary={opportunity.sector}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 600 }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </MotionCard>

              {/* Call to Action Card */}
              <MotionCard
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                elevation={4}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: 'white',
                  border: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`,
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.6)}`
                  }
                }}
              >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" gutterBottom fontWeight={600}>
                      Interested in this opportunity?
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                      Take the next step toward investment. Our team will connect you with the project owner to discuss further details.
                    </Typography>
                    
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={() => setContactModalOpen(true)}
                      disabled={!currentUser || currentUser.id === opportunity.userId}
                      sx={{
                        bgcolor: 'white',
                        color: theme.palette.primary.main,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.common.white, 0.9)
                        },
                        fontWeight: 600,
                        mb: 2
                      }}
                    >
                      Express Interest
                    </Button>

                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setContactModalOpen(true)}
                      disabled={!currentUser || currentUser.id === opportunity.userId}
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: alpha(theme.palette.common.white, 0.1)
                        }
                      }}
                    >
                      Request More Information
                    </Button>
                  </CardContent>
              </MotionCard>

              {/* Similar Opportunities */}
              {similarProjects.length > 0 && (
                <MotionCard
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  whileHover={{ y: -4, transition: { duration: 0.3 } }}
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.1)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.2)}`,
                      borderColor: alpha(theme.palette.warning.main, 0.3)
                    }
                  }}
                >
                  <Box
                    sx={{
                      height: 6,
                      background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`
                    }}
                  />
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle variant="h5">
                      Similar Opportunities
                    </SectionTitle>

                    <List disablePadding>
                      {similarProjects.map(opp => (
                        <ListItem
                          key={opp.id}
                          sx={{
                            px: 0,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            '&:last-child': {
                              borderBottom: 'none'
                            }
                          }}
                        >
                          <ListItemText
                            primary={opp.projectName}
                            secondary={`${opp.stage} • ${opp.country}`}
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                          <Button
                            variant="text"
                            onClick={() => navigate(`/marketplace/opportunity/${opp.id}`)}
                          >
                            View
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </MotionCard>
              )}
            </Grid>
          </Grid>
        </Container>

        <ContactModal
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          itemType="project"
          itemId={opportunity.id}
          itemName={opportunity.projectName}
          ownerName={ownerName}
          toUserId={opportunity.userId}
        />
      </MainContent>
    </Box>
  );
};

export default OpportunityDetailPage;